/**
 * Deeplink Switching entry page.
 *
 * Mounted at `/deeplink/switching/:code` (delegated from `DeepLink/index.js`).
 *
 * Flow:
 *  - URL `:code` is the source fund code (the fund the user wants to switch FROM).
 *  - Customer portfolio is fetched; the deeplinked fund is auto-selected as `from`.
 *  - User picks a destination fund (`to`) from the available fund list and an amount.
 *  - Risk profile / ECDD / sub-account status are gated identically to `/switching`.
 *  - Continue navigates to `/deeplink/switching/:code/summary/SWITCHING`.
 *
 * The page is wrapped in `SwitchingProvider` so the existing `useSwitching` context
 * (`switched`, `setSwitched`, `setResource`) drives the shared switch state used by
 * the (re-used) summary screen.
 */

import { InputAdornment, TextField, Typography } from "@mui/material";
import moment from "moment";
import React, { useEffect, useMemo, useRef, useState } from "react";
import NumberFormat from "react-number-format";
import { connect } from "react-redux";
import { withRouter } from "react-router";
import { compose } from "underscore";
import {
  Alert,
  AlertAccount,
  BackButton,
  CustomButton,
  CustomInput,
  DefaultLoader,
  Dropdown,
  NumberFormatCustom,
} from "../../components";
import { NOT_ALLOWED_ACCOUNT_STATUS } from "../../constants/subaccount-status";
import { SwitchingProvider, useSwitching } from "../../contexts/SwitchingContext";
import {
  getAccountStatusV2,
  getCustomerPortfolio,
  getSubAccountInvest,
} from "../dashboard/redux/actions";
import { createFundV2, getFundList, getScoreList } from "../fundSelection/redux/action";
import { checkWithdrawalMaxAmount } from "../redux/action";
import { getResultAssessment } from "../riskAssessment/redux/action";
import { SWITCHING, SWITCHING_TYPE } from "../redux/constant";
import { dataLayerGTM } from "../../utils/functions/dataLayerGTM";
import { dataMapping } from "../../utils/functions/dataMappingSwitching";
import { getRoundedValue } from "../../utils/functions/getRoundedValue";
import { useEcddChecking } from "../../utils/hooks";
import { isNotAllowSwitching } from "../../utils/headSubAccStsValidator";
import { MAX_SWITCH_AMOUNT, MIN_SWITCH_AMOUNT } from "../../utils/constants";
import { groupFunds } from "../../utils/functions/groupFunds";
import { icons } from "../../constants";
import "../switching/style.switch.scss";
import { getDeeplink4Config } from "./redux/action";
import { checkIfFundIsActive, checkIfFundIsTerminated } from "../../utils/functions/fundStatusChecker";
import { SWITCH_OUT_FUND_ERRORS, SwitchOutUnavailableAlert } from "../../components/SwitchOutUnavailableAlert";

const LEVEL_COLORS = ["#1278CC", "#0D629F", "#043964"];

function DeepLinkSwitchingInner(props) {
  const {
    match: { params },
    history,
    profile,
    customerPortfolio = [],
    fundList = [],
    scoreList= [],
    riskProfile = {},
    maxAmount = {},
    ecddStatus,
    subaccountDetail,
    loadingCustomerPortfolio,
    fundListLoading,
    scoreListLoading,
    loadingRiskProfile,
    loadingMaxAmount,
    subaccountInvestLoading,
    createFundLoadingV2,
    getCustomerPortfolio,
    getFundList,
    getScoreList,
    getResultAssessment,
    getSubAccountInvest,
    getAccountStatusV2,
    checkWithdrawalMaxAmount,
    createFundV2,
    getDeeplink4Config,
    deeplink4Config,
    deeplink4ConfigLoading,
    deeplink4ConfigError,
  } = props;

  const { code } = params;
  const {
    switched,
    setSwitched,
    setResource,
    isAlreadyRequestedCreatePortfolio,
  } = useSwitching();

  const [target, setTarget] = useState({ open: false });
  const [accountStatus, setAccountStatus] = useState("");
  const [subaccount, setSubaccount] = useState({
    "from-status": "",
    "to-status": "",
    open: false,
  });
  const [formStatus, setFormStatus] = useState("idle");
  const [riskProfileExpired, setRiskProfileExpired] = useState(false);
  const [switchOutFundError, setSwitchOutFundError] = useState("");

  const isSubmitting = formStatus === "submitting";
  const hasPrefilledFrom = useRef(false);
  const hasFetchedInitial = useRef(false);

  const { handleCheckEcdd, status, onResetStatus } = useEcddChecking({
    ecddStatus,
    aml: profile &&
      profile.amlHistory?.length > 0 &&
      typeof profile.amlHistory[0] === "object" && {
        result: profile.amlHistory[0].amlResult,
        status: profile.amlHistory[0].amlSts,
      },
  });

  const maxSwitchableAmount = useMemo(
    () => getRoundedValue(maxAmount?.totalAmount || 0, 2),
    [maxAmount]
  );

  const lessThan = Boolean(switched.amount && switched.amount < MIN_SWITCH_AMOUNT);
  const biggerThan = Boolean(switched.amount > MAX_SWITCH_AMOUNT);
  const biggerThanMaxAmount = Boolean(
    !lessThan &&
      getRoundedValue(switched.amount, 2) > maxSwitchableAmount
  );

  const handleCheckAccountStatus = () => {
    if (NOT_ALLOWED_ACCOUNT_STATUS.includes(profile?.headAccount?.sts)) {
      setAccountStatus(profile.headAccount.sts);
      return "blocked";
    }
    if (NOT_ALLOWED_ACCOUNT_STATUS.includes(subaccountDetail?.sts)) {
      setAccountStatus(subaccountDetail?.sts);
      return "blocked";
    }
    return null;
  };

  // Fetch customer portfolio + fund list + score list + risk profile on mount
  useEffect(() => {
    if (hasFetchedInitial.current) return;
    if (!profile?.masterId) return;
    hasFetchedInitial.current = true;

    getCustomerPortfolio();
    getFundList({});
    getScoreList();
    getDeeplink4Config();
    getResultAssessment(profile.masterId);
  }, [profile?.masterId]);

  const config = useMemo(() => {
    try {
      return JSON.parse((deeplink4Config || {}).value || "{}");
    } catch (e) {
      return {};
    }
  }, [deeplink4Config]);

  const fromFundCodes = useMemo(() => {
    return (config?.fromFunds || []).map(c => c.toUpperCase());
  }, [config]);

  // List of destination funds (exclude the one we're switching from)
  const toFundOptions = useMemo(() => {
    const toFundCodes = (config?.toFunds || []).map(c => c.toUpperCase());
    if (
      !Array.isArray(fundList) ||
      fundList.length === 0 ||
      !Array.isArray(toFundCodes) ||
      toFundCodes.length === 0
    )
      return [];
    const funds = fundList
      .filter(
        f =>
          (f?.code || f?.id || "").toUpperCase() !==
            (code || "").toUpperCase() &&
          toFundCodes.includes((f?.code || f?.id || "").toUpperCase()) &&
          checkIfFundIsActive(f)
      )
      .sort(
        (a, b) =>
          toFundCodes.indexOf((a?.code || a?.id || "").toUpperCase()) -
          toFundCodes.indexOf((b?.code || b?.id || "").toUpperCase())
      );
    return groupFunds({ funds, scoreList: scoreList || [] }) || [];
  }, [fundList, scoreList, code, config]);

  // Prefill `from` once portfolio arrives
  useEffect(() => {
    if (hasPrefilledFrom.current) return;
    if (!Array.isArray(customerPortfolio) || customerPortfolio.length === 0) {
      return;
    }
    if (!code) return;

    const upperCode = code.toUpperCase();

    // Wait for deeplink config before validating the fund code.
    if (fromFundCodes.length === 0) return;

    if (!fromFundCodes.includes(upperCode)) {
      history.push("/");
      return;
    }

    const sourceHolding = customerPortfolio.find(
      p => (p?.fundCd || "").toUpperCase() === upperCode
    );

    if (!sourceHolding || sourceHolding?.switchableAmount <= 0) {
      // user has no holding in this fund - cannot switch from it
      setSwitchOutFundError(SWITCH_OUT_FUND_ERRORS.NO_HOLDING);
      return;
    }

    if (checkIfFundIsTerminated(sourceHolding?.fund)) {
      // fund is terminated - cannot switch from it
      setSwitchOutFundError(SWITCH_OUT_FUND_ERRORS.FUND_TERMINATED_DEEPLINK);
      return;
    }

    hasPrefilledFrom.current = true;

    const fromData = dataMapping({
      ...sourceHolding,
      type: "from",
      productType: "invest",
    });

    setSwitched(prev => ({
      ...prev,
      cifid: profile?.masterId || prev.cifid,
      from: {
        cifPlanId: fromData.cifPlanId,
        fundCd: fromData.fundCd,
        name: fromData.name,
        switchableAmount: fromData.switchableAmount,
        productType: "invest",
        accountNo: fromData.accountNo,
        fee: fromData.fee,
      },
    }));

    // fetch sub-account status + max amount for the source holding
    getSubAccountInvest(fromData.accountNo, subaccountData => {
      setSubaccount(prev => ({
        ...prev,
        "from-status": subaccountData?.sts,
      }));
    });
    checkWithdrawalMaxAmount(
      `${fromData.cifPlanId}?fundCd=${fromData.fundCd}`
    );
  }, [customerPortfolio, code, profile?.masterId, fromFundCodes]);


  const isLoading =
    loadingCustomerPortfolio ||
    fundListLoading ||
    scoreListLoading ||
    loadingRiskProfile ||
    loadingMaxAmount ||
    subaccountInvestLoading ||
    createFundLoadingV2 ||
    deeplink4ConfigLoading;

  const forbiddenSubaccount = useMemo(
    () =>
      isNotAllowSwitching(
        subaccount["from-status"],
        subaccount["to-status"]
      ),
    [subaccount]
  );

  const checkRiskProfile = () => {
    const riskProfileLength = Object.values(riskProfile || {}).length;

    if (riskProfileLength === 0) {
      // user doesnt have risk profile - send to risk assessment
      // payload mirrors what `useSwitching().handleInvestIntro` does so the user can
      // come back and resume the deeplink switching flow
      setResource({
        from: { ...switched.from },
        to: { ...switched.to },
        amount: switched.amount,
        maxAmount: switched.maxAmount,
        cifid: switched.cifid,
      });
      history.push("/risk-assessment/deeplink", {
        type: SWITCHING_TYPE,
        code,
      });
      return false;
    }

    if (
      riskProfileLength > 0 &&
      new Date() >=
        moment(riskProfile.expiredDate)
          .startOf("date")
          .toDate()
    ) {
      setRiskProfileExpired(true);
      return false;
    }

    return true;
  };

  const handlePickTo = toItem => () => {
    // build "to" payload using same shape as switching/index.js handleSwitched
    const fundCd = toItem?.code || toItem?.id;
    const name = toItem?.name;

    if (!checkRiskProfile()) return;

    const portfolio = customerPortfolio[0] || {};

    const setSwitchedTo = (cifPlanId, accountNo) => {
      setSwitched(prev => ({
        ...prev,
        to: {
          cifPlanId,
          fundCd,
          name,
          productType: "invest",
          accountNo,
        },
      }));
      setTarget({ open: false });
    };

    // Invest accountNo / cifPlanId is the same across all invest funds within a
    // portfolio - reuse the existing portfolio if present, otherwise create one.
    if (portfolio?.cifPlanId && portfolio?.accountNo) {
      setSwitchedTo(portfolio.cifPlanId, portfolio.accountNo);
      return;
    }

    if (isAlreadyRequestedCreatePortfolio?.current) return;

    createFundV2(
      {
        cifId: switched.cifid || profile?.masterId,
        fundsPayload: [{ fundCode: fundCd }],
      },
      created => {
        if (created) {
          isAlreadyRequestedCreatePortfolio.current = true;
          setSwitchedTo(created.cifPlanId, created.accountNo);

          getSubAccountInvest(created.accountNo, subaccountData => {
            setSubaccount(prev => ({
              ...prev,
              "to-status": subaccountData?.sts,
            }));
          });
        }
      }
    );
  };

  const handleAmountChange = ({ target: { value } }) => {
    setSwitched(prev => ({ ...prev, amount: value }));
  };

  const handleMaxAmount = () => {
    if (!switched.from?.name) return;
    setSwitched(prev => ({
      ...prev,
      amount: maxSwitchableAmount,
      maxAmount: maxSwitchableAmount,
    }));
  };

  const handleContinue = () => {
    if (formStatus === "submitted") return;

    if (!checkRiskProfile()) return;

    if (
      lessThan ||
      biggerThan ||
      biggerThanMaxAmount ||
      !switched.from?.name ||
      !switched.to?.name
    ) {
      setFormStatus("submitting");
      return;
    }

    if (forbiddenSubaccount) {
      setSubaccount(prev => ({ ...prev, open: true }));
      return;
    }

    const isAccountBlocked = handleCheckAccountStatus();
    if (isAccountBlocked) return;

    setFormStatus("submitted");

    // The summary route mounts a fresh SwitchingProvider (different React tree),
    // so we persist the switched payload into the switchingReducer via setResource
    // and the summary screen reads it back from redux.
    const persistedResource = {
      ...switched,
      maxAmount: maxSwitchableAmount,
      cifid: switched.cifid || profile?.masterId,
    };
    setSwitched(prev => ({ ...prev, maxAmount: maxSwitchableAmount }));
    setResource(persistedResource);

    dataLayerGTM(
      "click_tag",
      "Click",
      "Button",
      "Deeplink Switching - Continue"
    );

    history.push(`/deeplink/${SWITCHING_TYPE}/${code}/summary/${SWITCHING}`);
  };

  if (!!switchOutFundError) {
    return (
      <SwitchOutUnavailableAlert
        switchOutFundError={switchOutFundError}
        isOpen
        onClose={() => history.push("/dashboard")}
      />
    );
  }

  if (isLoading || !switched.from?.name) {
    return <DefaultLoader />;
  }

  return (
    <>
      <Alert
        title="Your risk profile is expired"
        description="Risk Profile has expired, please perform Risk Assessment"
        isOpen={riskProfileExpired}
        isCancel
        btnTxt="Ok"
        handleAction={() => {
          // Persist switch state so user can resume after the assessment.
          setResource({
            from: { ...switched.from },
            to: { ...switched.to },
            amount: switched.amount,
            maxAmount: switched.maxAmount,
            cifid: switched.cifid,
          });
          // Skip /invest-introduction (invest-only) and go straight to the
          // deeplink risk assessment, which redirects back to
          // `/deeplink/switching/:code` on completion.
          history.push("/risk-assessment/deeplink", {
            type: SWITCHING_TYPE,
            code,
            expired: true,
          });
        }}
        handleCancel={() => setRiskProfileExpired(false)}
      />

      <Alert
        title="Account Verification"
        description="Thank you for submitting your information. We need to perform further verification to activate your account."
        isOpen={status === "pending"}
        isCS="https://principal.com.my"
        isCancel={false}
        btnTxt="Chat with us on WhatsApp"
        handleAction={() => {
          onResetStatus();
          window.my.call("openAppLink", {
            url: "https://wa.me/60162996213",
          });
        }}
        handleCancel={onResetStatus}
      />

      <Alert
        title="Your account is deactivated"
        description="So sorry, your account is deactivated. We need to perform a verification to activate your account."
        isOpen={status === "rejected"}
        isCS="https://principal.com.my"
        isCancel={false}
        btnTxt="Chat with us on WhatsApp"
        handleAction={() => {
          onResetStatus();
          window.my.call("openAppLink", {
            url: "https://wa.me/60162996213",
          });
        }}
        handleCancel={onResetStatus}
      />

      <AlertAccount
        title={`Account ${
          accountStatus === "Suspended" ? "Suspended" : "Inactive"
        }`}
        firstText={
          accountStatus === "Suspended"
            ? "Your account is currently suspended, we are unable to process your switch request."
            : "Your account is currently inactive, we're sorry for the inconvenience."
        }
        secondText={
          <span>
            For assistance, kindly contact our{" "}
            <span
              onClick={() => {
                setAccountStatus("");
                window.my.call("openAppLink", {
                  url: "https://wa.me/60162996213",
                });
              }}
              style={{ color: "#1278CC" }}
            >
              Customer Care Centre.
            </span>
          </span>
        }
        isOpen={NOT_ALLOWED_ACCOUNT_STATUS.includes(accountStatus)}
        handleClose={() => setAccountStatus("")}
      />

      <AlertAccount
        title="Account Inactive"
        firstText="Your account is currently inactive, we're sorry for the inconvenience."
        secondText={
          <span>
            For assistance, kindly contact our{" "}
            <span
              onClick={() => {
                window.my.call("openAppLink", {
                  url: "https://wa.me/60162996213",
                });
              }}
              style={{ color: "#1278CC" }}
            >
              Customer Care Centre.
            </span>
          </span>
        }
        isOpen={subaccount.open}
        handleClose={() => setSubaccount(prev => ({ ...prev, open: false }))}
      />

      <Dropdown
        isOpen={target.open}
        handleDismiss={() => setTarget({ open: false })}
        snapPoints={({ maxHeight }) => maxHeight - maxHeight / 28}
        header={
          <div className="l-switch__title">
            <h1>Select a fund to switch to</h1>
          </div>
        }
      >
        <>
        {/* <li
            key={fund.code || fund.fundCd}
            onClick={handlePickTo(fund)}
            style={{
              padding: "16px",
              borderBottom: "1px solid #eee",
              cursor: "pointer",
            }}
          >
            <p style={{ margin: 0, fontWeight: 600 }}>{fund.name}</p>
            {fund.assetClass && (
              <p style={{ margin: "4px 0 0", fontSize: 12, color: "#666" }}>
                {fund.assetClass}
              </p>
            )}
          </li> */}
          {Object.keys(toFundOptions)
          .map((title, riskIndex) => (
            toFundOptions[title].length > 0 && (
              <div key={title} className="c-fund-group">
                <h3
                  style={{
                    color: LEVEL_COLORS[riskIndex],
                  }}
                >
                  {title}
                </h3>
                {toFundOptions[title].map((fund, idx) => {

                  return (
                    <div
                      key={idx}
                      className="c-card-switch c-card-switch--no-inline-padding"
                      onClick={handlePickTo(fund)}
                    >
                      <h4>{fund.name}</h4>
                      {switched["to"].fundCd === fund.code &&
                          <img src={icons.check} alt="check" />}
                    </div>
                  );
                })}
              </div>)
            ))
        }
        </>
      </Dropdown>

      <div className="l-switch" style={{ padding: 16 }}>
        <BackButton
          handleBack={() => history.back()}
          title="Switch"
        />

        <div>

          <Typography
            color="textPrimary"
            sx={{
              fontWeight: "700",
              fontSize: "14px",
              lineHeight: 1.57,
              marginBottom: "4px",
            }}
          > From</Typography>

          <Typography
            color="textPrimary"
            sx={{
              fontWeight: "700",
              fontSize: "14px",
              lineHeight: 1.57,
              marginBottom: "16px",
              color: "#616367",
            }}
          > {switched.from?.name || ""}</Typography>

          <CustomInput
            label="To"
            name="to"
            placeholder="Select Fund"
            value={switched.to?.name || ""}
            isEdit
            isDropdown
            onClick={handleCheckEcdd({
              cb: () => {
                if (!checkRiskProfile()) return;
                setTarget({ open: true });
              },
              ecddExpiredCb: () =>
                history.push("/onBoarding/ecdd/DEEPLINK", {
                  type: SWITCHING_TYPE,
                  code,
                }),
              gtm: {
                event: "click_tag",
                action: "Click",
                category: "Button",
                label: "Deeplink Switching - Open To Dropdown",
              },
            })}
            error={isSubmitting && !switched.to?.name}
            className="c-input--ellipsis"
          />

          <div className="l-switch__separator" />

          <label className="c-input__label">Please enter an amount</label>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="0.00 (minimum RM10)"
            value={switched.amount === "0" ? "" : switched.amount || ""}
            onChange={handleAmountChange}
            error={
              isSubmitting && (lessThan || biggerThan || biggerThanMaxAmount)
            }
            helperText={
              isSubmitting &&
              (lessThan
                ? "Minimum RM 10.00"
                : biggerThan
                ? "Maximum RM 1,000,000,000,000.00"
                : biggerThanMaxAmount &&
                  "Switched amount cannot exceed your max amount")
            }
            FormHelperTextProps={{ className: "c-input__text" }}
            inputProps={{ className: "c-input c-input--outline" }}
            // eslint-disable-next-line react/jsx-no-duplicate-props
            InputProps={{
              inputComponent: NumberFormatCustom,
              className: "c-input__wrapper",
              startAdornment: (
                <InputAdornment position="start">
                  <p className="c-input__start-adornment-text">RM</p>
                </InputAdornment>
              ),
              endAdornment: (
                <div
                  className="c-input__end-adornment"
                  onClick={handleMaxAmount}
                >
                  <p className="c-input__end-adornment-text">Max Amount</p>
                </div>
              ),
            }}
          />

          <h4 className="l-switch__switchable-amount">
            Switchable amount:{" "}
            <span>
              RM{" "}
              <NumberFormat
                value={maxSwitchableAmount || 0}
                thousandSeparator
                displayType="text"
                decimalScale={2}
                fixedDecimalScale
              />
            </span>
          </h4>

          <p className="l-switch__info">
            Full Cash Out will be initiated if the balance of units are below
            the minimum unit holding balance threshold.
          </p>
        </div>

        <div
          style={{
            position: "sticky",
            bottom: 0,
            width: "100%",
            display: "block",
            marginTop: "auto",
          }}
        >
          <CustomButton
            text="Continue"
            variant="contained"
            color="#fff"
            backgroundColor="#1278CC"
            onClick={handleCheckEcdd({
              cb: handleContinue,
              ecddExpiredCb: () =>
                history.push("/onBoarding/ecdd/DEEPLINK", {
                  type: SWITCHING_TYPE,
                  code,
                }),
              gtm: {
                event: "click_tag",
                action: "Click",
                category: "Button",
                label: "Deeplink Switching - Continue Button",
              },
            })}
          />
        </div>
      </div>
    </>
  );
}

const mapStateToProps = state => ({
  profile: state.goal.profile,
  ecddStatus: state.goal.ecddStatus,
  customerPortfolio: state.dashboardReducer.customerPortfolio,
  loadingCustomerPortfolio: state.dashboardReducer.loadingCustomerPortfolio,
  subaccountInvestLoading: state.dashboardReducer.subaccountInvestLoading,
  fundList: state.fundSelectionReducer.fundList,
  fundListLoading: state.fundSelectionReducer.fundListLoading,
  createFundLoadingV2: state.fundSelectionReducer.createFundLoadingV2,
  riskProfile: state.riskAssessmentReducer.riskProfileResult,
  loadingRiskProfile: state.riskAssessmentReducer.loadingRiskProfile,
  maxAmount: state.goal.maxAmount,
  loadingMaxAmount: state.goal.loadingMaxAmount,
  subaccountDetail: state.goalDetailReducer.subAccountDetail,
  scoreList: state.fundSelectionReducer.scoreList || [],
  scoreListLoading: state.fundSelectionReducer.scoreListLoading,
  deeplink4Config: state.deeplinkReducer.deeplink4Config,
  deeplink4ConfigLoading: state.deeplinkReducer.deeplink4ConfigLoading,
  deeplink4ConfigError: state.deeplinkReducer.deeplink4ConfigError,
});

const mapDispatchToProps = {
  getCustomerPortfolio,
  getFundList,
  getScoreList,
  getResultAssessment,
  getSubAccountInvest,
  getAccountStatusV2,
  getDeeplink4Config,
  checkWithdrawalMaxAmount,
  createFundV2,
};

const ConnectedInner = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps)
)(DeepLinkSwitchingInner);

/**
 * Public component - wraps the inner page with `SwitchingProvider` so that
 * `useSwitching` works for the deeplink switching path. The summary screen
 * (re-used for both `/switching/summary` and `/deeplink/.../summary/SWITCHING`)
 * also relies on this context.
 */
function DeepLinkSwitching(props) {
  return (
    <SwitchingProvider>
      <ConnectedInner {...props} />
    </SwitchingProvider>
  );
}

export default DeepLinkSwitching;
